import { h } from 'preact';
import { OrthomolIcon } from '~/components-common/OrthomolIcon';
import { URLS } from '../Urls';
import type { OrderLineItem as OrderLineItemType } from '../shopware/shopwareApi';
import { formatPrice } from '../shopware/shopwareUtils';
import { useOrders } from './OrdersContext';

interface OrderLineItemProps {
  item: OrderLineItemType;
}

export const OrderLineItem = ({ item }: OrderLineItemProps) => {
  const { getLineItemDetails, isLoadingDetails } = useOrders();

  // Check if this is a promotion
  const isPromotion = item.type === 'promotion';

  // Render promotion
  if (isPromotion) {
    // For promotions: check if type is percentage, then display percentage only, otherwise display totalPrice
    const priceDefinition = item.priceDefinition as any;
    const isPercentagePromotion = priceDefinition?.type === 'percentage' && priceDefinition.percentage !== undefined;
    const promotionDisplay = isPercentagePromotion
      ? `${priceDefinition.percentage}%`
      : formatPrice(item.totalPrice) + ' €';

    return (
      <div className="bg-gray-200 d-flex gap-2 align-items-center px-3 py-3 mb-3">
        <span className="text-primary">
          <OrthomolIcon name="voucher-24" size="24" ariaHidden={true} />
        </span>
        <h6 className="text-primary mb-0 flex-grow-1 ms-2">{item.label}</h6>
        <strong className="shopping-cart-item-promotion text-nowrap">{promotionDisplay}</strong>
      </div>
    );
  }

  // Render regular product
  // Generate redirect URL using productNumber from payload, same as CartProductListItem
  const productUrl = item.payload?.productNumber ? URLS.productRedirectWithONumber(item.payload.productNumber) : '#';

  // Get details (points, dosageFormName, size) for this line item
  const productNumber = item.payload?.productNumber;
  const details = productNumber && typeof productNumber === 'string' ? getLineItemDetails(productNumber) : null;
  const itemPoints = details?.points ? details.points * (item.quantity || 1) : 0;

  // Build subtitle from dosageFormName and size
  const subtitle =
    details?.dosageFormName || details?.size
      ? [details.dosageFormName, details.size].filter(Boolean).join(', ')
      : null;

  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div className="d-flex gap-3">
        <div className="order-product-image flex-shrink-0">
          <a href={productUrl}>
            <img
              src={item.cover?.url || '/placeholder.jpg'}
              alt={item.label}
              className="img-fluid"
              style={{ width: '80px', height: '80px', objectFit: 'contain' }}
            />
          </a>
        </div>
        <div className="order-product-details flex-grow-1">
          <h4 className="h6 mb-1">
            <a href={productUrl} className="text-decoration-none">
              {item.label}
            </a>
          </h4>
          {!isLoadingDetails && subtitle && <div className="small text-muted mb-1">{subtitle}</div>}
          <div className="small text-muted mb-2">Menge: {item.quantity}</div>
          <div className="fw-bold">{formatPrice(item.totalPrice)} &euro;</div>
          {!isLoadingDetails && itemPoints > 0 && (
            <div className="small text-muted">+{itemPoints.toLocaleString('de-DE')} Treuepunkte</div>
          )}
        </div>
      </div>
    </div>
  );
};
